/**
 * Home screen — workflow selector with four tiles + quick asset lookup.
 */

import { router } from '../router.js';
import { isConfigured } from '../settings.js';
import { pendingCount, enqueue } from '../db.js';
import { getAssetByTag, getAssetBySerial, getAsset, getModels, getStatusLabels } from '../snipeit.js';
import { syncDirect } from '../sync.js';
import { startScanner, stopScanner } from './scanner.js';
import { showToast } from '../toast.js';
import { hapticTap, hapticSuccess } from '../haptics.js';
import { MAC_COLUMN_WLAN, MAC_COLUMN_LAN, MAC_COLUMN_IP } from '../settings.js';
import { getPresets, unpinPreset, pinRecent, deleteRecent, buildPresetLabel } from '../browse-presets.js';

export async function renderHome() {
  const app = document.getElementById('app');
  const count = await pendingCount();

  app.innerHTML = `
    <div class="page">
      <div class="home-header">
        <h1>SnipeySnipe</h1>
        <button class="back-btn" id="settings-btn" aria-label="Settings">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="3"/>
            <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/>
          </svg>
        </button>
      </div>

      ${!isConfigured() ? `
        <div class="card" style="margin-bottom: 16px; border-color: var(--color-warning);">
          <p style="font-size: 0.9rem;">
            <strong>Setup required.</strong> Tap the gear icon to configure your Snipe-IT server URL and API key.
          </p>
        </div>
      ` : ''}

      <div class="quick-lookup">
        <label class="quick-lookup-label">Quick Lookup</label>
        <div class="quick-lookup-row">
          <div class="search-wrap">
            <span class="search-icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>
            </span>
            <input type="text" id="ql-input" class="form-control" placeholder="Asset tag or serial number..." inputmode="text" />
          </div>
          <button class="btn btn-secondary btn-sm" id="ql-scan-btn" aria-label="Scan barcode">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="7" y1="12" x2="17" y2="12"/></svg>
          </button>
          <button class="btn btn-primary btn-sm" id="ql-go-btn">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            Go
          </button>
        </div>
        <div id="ql-scanner-area" class="hidden" style="margin-top:8px;">
          <div class="camera-container" id="ql-scanner-reader"></div>
          <button class="btn btn-secondary btn-block btn-sm" id="ql-scanner-close">Close Scanner</button>
        </div>
      </div>

      <div class="workflow-grid">
        <a href="#/add-asset" class="workflow-tile workflow-tile--add">
          <span class="tile-icon">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round">
              <rect x="2" y="3" width="20" height="18" rx="2"/>
              <line x1="12" y1="8" x2="12" y2="16"/>
              <line x1="8" y1="12" x2="16" y2="12"/>
            </svg>
          </span>
          <span class="tile-content">
            <span class="tile-label">Add Asset</span>
            <span class="tile-subtitle">Scan label or enter manually</span>
          </span>
          <span class="tile-chevron" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
          </span>
        </a>

        <a href="#/checkout" class="workflow-tile workflow-tile--checkout">
          <span class="tile-icon">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round">
              <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/>
              <polyline points="16 17 21 12 16 7"/>
              <line x1="21" y1="12" x2="9" y2="12"/>
            </svg>
          </span>
          <span class="tile-content">
            <span class="tile-label">Check Out</span>
            <span class="tile-subtitle">Assign to user, location, or asset</span>
          </span>
          <span class="tile-chevron" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
          </span>
        </a>

        <a href="#/checkin" class="workflow-tile workflow-tile--checkin">
          <span class="tile-icon">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round">
              <path d="M15 3h4a2 2 0 012 2v14a2 2 0 01-2 2h-4"/>
              <polyline points="10 17 15 12 10 7"/>
              <line x1="15" y1="12" x2="3" y2="12"/>
            </svg>
          </span>
          <span class="tile-content">
            <span class="tile-label">Check In</span>
            <span class="tile-subtitle">Return asset to inventory</span>
          </span>
          <span class="tile-chevron" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
          </span>
        </a>

        <a href="#/edit-asset" class="workflow-tile workflow-tile--edit">
          <span class="tile-icon">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round">
              <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
              <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
            </svg>
          </span>
          <span class="tile-content">
            <span class="tile-label">Edit Asset</span>
            <span class="tile-subtitle">Modify asset details</span>
          </span>
          <span class="tile-chevron" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
          </span>
        </a>
      </div>

      <div id="saved-searches-section"></div>

      <div style="padding: 4px 0 0;">
        <button class="btn btn-primary btn-block" id="browse-all-btn" style="border-radius: var(--radius-md); height: 48px; font-size: 0.9375rem;">
          Browse All Assets \u2192
        </button>
      </div>

      ${count > 0 ? `
        <div class="card" style="margin-top: 20px; text-align: center;">
          <p style="font-size: 0.9rem; margin-bottom: 8px;">
            <span class="queue-badge">${count}</span> item${count !== 1 ? 's' : ''} in queue
          </p>
          <div style="display:flex; gap:8px; justify-content:center;">
            <button class="btn btn-primary btn-sm" id="open-queue-btn">View Queue</button>
            <button class="btn btn-secondary btn-sm" id="open-history-btn">History</button>
          </div>
        </div>
      ` : `
        <div class="card" style="margin-top: 20px; text-align: center;">
          <button class="btn btn-secondary btn-sm" id="open-history-btn">Sync History</button>
        </div>
      `}
    </div>
  `;

  // ── Settings ──
  document.getElementById('settings-btn')?.addEventListener('click', () => {
    router.navigate('/settings');
  });

  // ── Queue / History ──
  document.getElementById('open-queue-btn')?.addEventListener('click', () => {
    window.dispatchEvent(new CustomEvent('open-queue'));
  });

  document.getElementById('open-history-btn')?.addEventListener('click', () => {
    window.dispatchEvent(new CustomEvent('open-queue', { detail: { tab: 'history' } }));
  });

  // ── Browse All ──
  document.getElementById('browse-all-btn')?.addEventListener('click', () => {
    router.navigate('/browse');
  });

  // ── Saved Searches ──
  renderSavedSearches();

  function renderSavedSearches() {
    const container = document.getElementById('saved-searches-section');
    if (!container) return;
    const { pinned, recents } = getPresets();
    const hasAny = pinned.length > 0 || recents.length > 0;

    if (!hasAny) {
      container.innerHTML = '';
      return;
    }

    const pinnedHtml = pinned.map(p => `
      <div class="preset-card pinned" data-preset-id="${p.id}" data-filters='${JSON.stringify(p.filters).replace(/'/g, '&#39;')}' data-labels='${JSON.stringify(p.labels).replace(/'/g, '&#39;')}'>
        <span class="preset-icon">\ud83d\udccc</span>
        <span class="preset-body">
          <span class="preset-name">${escapeHtml(p.name)}</span>
          <span class="preset-sub">${escapeHtml(buildPresetLabel(p.labels))}</span>
        </span>
        <button class="preset-action unpin-btn" data-id="${p.id}" aria-label="Unpin">\u2715</button>
      </div>
    `).join('');

    const recentHtml = recents.slice(0, 3).map(r => `
      <div class="preset-card" data-preset-id="${r.id}" data-filters='${JSON.stringify(r.filters).replace(/'/g, '&#39;')}' data-labels='${JSON.stringify(r.labels).replace(/'/g, '&#39;')}'>
        <span class="preset-icon">\ud83d\udd70</span>
        <span class="preset-body">
          <span class="preset-name">${escapeHtml(buildPresetLabel(r.labels))}</span>
          <span class="preset-sub">Recent search</span>
        </span>
        <button class="preset-action pin-btn" data-id="${r.id}" aria-label="Pin">\u2606</button>
      </div>
    `).join('');

    container.innerHTML = `
      <div style="margin: 16px 0 8px;">
        <div class="quick-lookup-label">My Saved Searches</div>
        ${pinnedHtml}
        ${recentHtml}
      </div>
    `;

    // Preset card tap → navigate to browse with filters
    container.querySelectorAll('.preset-card').forEach(card => {
      card.addEventListener('click', (e) => {
        if (e.target.closest('.preset-action')) return;
        const f = JSON.parse(card.dataset.filters);
        const l = JSON.parse(card.dataset.labels);
        sessionStorage.setItem('browse_prefill', JSON.stringify({ filters: f, labels: l }));
        router.navigate('/browse');
      });
    });

    // Unpin
    container.querySelectorAll('.unpin-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        unpinPreset(btn.dataset.id);
        renderSavedSearches();
        showToast('Unpinned');
      });
    });

    // Pin recent
    container.querySelectorAll('.pin-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const card = btn.closest('.preset-card');
        const lbl = buildPresetLabel(JSON.parse(card.dataset.labels));
        const name = prompt('Name this shortcut:', lbl);
        if (name === null) return;
        pinRecent(btn.dataset.id, name.trim() || lbl);
        renderSavedSearches();
        showToast('Pinned');
      });
    });
  }

  // ── Quick Lookup ──
  const qlInput = document.getElementById('ql-input');
  const qlGoBtn = document.getElementById('ql-go-btn');

  qlGoBtn.addEventListener('click', () => triggerLookup());

  qlInput.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') { e.preventDefault(); triggerLookup(); }
  });

  // Scanner
  document.getElementById('ql-scan-btn').addEventListener('click', () => {
    hapticTap();
    const area = document.getElementById('ql-scanner-area');
    area.classList.remove('hidden');
    startScanner('ql-scanner-reader', (text) => {
      hapticSuccess();
      stopScanner();
      area.classList.add('hidden');
      qlInput.value = text;
      setTimeout(() => triggerLookup(), 100);
    }, { narrow: true });
  });

  document.getElementById('ql-scanner-close').addEventListener('click', () => {
    stopScanner();
    document.getElementById('ql-scanner-area').classList.add('hidden');
  });

  async function triggerLookup() {
    const val = qlInput.value.trim();
    if (!val) return;

    qlGoBtn.disabled = true;
    qlGoBtn.innerHTML = '<span class="spinner" style="width:14px;height:14px;border-width:2px;"></span>';

    try {
      document.activeElement?.blur();

      const [tagResult, serialResult] = await Promise.allSettled([
        getAssetByTag(val),
        getAssetBySerial(val),
      ]);

      // Resolve settled results — null means not found, rejection means network/auth error
      const tagAsset = tagResult.status === 'fulfilled' ? tagResult.value : null;
      const serialRaw = serialResult.status === 'fulfilled' ? serialResult.value : null;
      // /hardware/byserial/ may return { rows, total } or a bare asset object depending on Snipe-IT version
      const serialAsset = serialRaw?.rows ? (serialRaw.rows[0] ?? null) : serialRaw;

      const bothFailed = tagResult.status === 'rejected' && serialResult.status === 'rejected';
      if (bothFailed) {
        showToast('Lookup failed');
        return;
      }

      if (!tagAsset && !serialAsset) {
        showNotFoundModal(val);
      } else if (tagAsset && serialAsset && tagAsset.id !== serialAsset.id) {
        showMultipleFoundModal([tagAsset, serialAsset]);
      } else {
        showAssetFoundModal(tagAsset ?? serialAsset);
      }
    } finally {
      qlGoBtn.disabled = false;
      qlGoBtn.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg> Go';
    }
  }

  // ── Multiple Found Modal ──
  function showMultipleFoundModal(assets) {
    document.querySelectorAll('[data-multi-modal]').forEach(el => el.remove());
    const backdrop = document.createElement('div');
    backdrop.className = 'bottom-sheet-backdrop';
    backdrop.dataset.multiModal = 'true';
    const sheet = document.createElement('div');
    sheet.className = 'bottom-sheet';
    sheet.dataset.multiModal = 'true';
    document.body.appendChild(backdrop);
    document.body.appendChild(sheet);

    function close() {
      backdrop.classList.remove('open');
      sheet.classList.remove('open');
      setTimeout(() => { backdrop.remove(); sheet.remove(); }, 300);
    }

    sheet.innerHTML = `
      <div class="sheet-handle"></div>
      <div class="sheet-header">
        <h2>Multiple matches</h2>
      </div>
      <div class="sheet-body" style="padding:16px;">
        <p style="font-size:0.9rem; color:var(--color-text-muted); margin:0 0 12px;">Select the asset you were looking for:</p>
        <div id="ql-multi-list" style="display:flex;flex-direction:column;gap:8px;margin-bottom:12px;"></div>
      </div>
      <div class="sheet-footer">
        <button class="btn btn-secondary" style="flex:1" id="ql-multi-cancel">Cancel</button>
      </div>
    `;

    const list = sheet.querySelector('#ql-multi-list');
    assets.forEach(asset => {
      const btn = document.createElement('button');
      btn.className = 'btn btn-secondary btn-block';
      btn.textContent = `${asset.asset_tag} — ${asset.model?.name ?? 'Unknown model'}`;
      btn.addEventListener('click', () => {
        close();
        setTimeout(() => showAssetFoundModal(asset), 310);
      });
      list.appendChild(btn);
    });

    sheet.querySelector('#ql-multi-cancel').addEventListener('click', close);
    backdrop.addEventListener('click', close);
    sheet.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });

    requestAnimationFrame(() => {
      backdrop.classList.add('open');
      sheet.classList.add('open');
    });
  }

  // ── Asset Found Modal ──
  function showAssetFoundModal(asset) {
    const backdrop = document.createElement('div');
    backdrop.className = 'bottom-sheet-backdrop';
    const sheet = document.createElement('div');
    sheet.className = 'bottom-sheet';
    sheet.style.maxHeight = '85dvh';
    document.body.appendChild(backdrop);
    document.body.appendChild(sheet);

    let currentAsset = asset;

    function close() {
      backdrop.classList.remove('open');
      sheet.classList.remove('open');
      setTimeout(() => { backdrop.remove(); sheet.remove(); }, 300);
    }

    function renderDetail() {
      sheet.style.maxHeight = '85dvh';
      const a = currentAsset;
      const wlan = a.custom_fields?.[MAC_COLUMN_WLAN]?.value || '';
      const lan = a.custom_fields?.[MAC_COLUMN_LAN]?.value || '';
      const ip = a.custom_fields?.[MAC_COLUMN_IP]?.value || '';
      const statusName = a.status_label?.name || '\u2014';
      const statusType = a.status_label?.status_type || '';
      const statusClass = statusType === 'deployable' ? 'deployable'
        : statusType === 'pending' ? 'pending'
        : statusType === 'archived' ? 'archived' : '';

      sheet.innerHTML = `
        <div class="sheet-handle"></div>
        <div class="sheet-header">
          <h2>Asset Details</h2>
          <button class="btn btn-secondary btn-sm" id="ql-modal-close-top">Close</button>
        </div>
        <div class="sheet-body" style="padding:16px;">
          <h3 style="margin:0 0 4px; font-size:1.05rem;">${escapeHtml(a.name || 'Unnamed')}</h3>
          <div style="font-size:0.85rem; color:var(--color-text-muted); margin-bottom:14px;">
            Tag: <span class="mono">${escapeHtml(a.asset_tag || '\u2014')}</span>
            &nbsp;|&nbsp; Serial: <span class="mono">${escapeHtml(a.serial || '\u2014')}</span>
          </div>
          <dl class="asset-detail-dl">
            <dt>Model</dt><dd>${escapeHtml(a.model?.name || '\u2014')}</dd>
            <dt>Category</dt><dd>${escapeHtml(a.category?.name || '\u2014')}</dd>
            <dt>Status</dt><dd><span class="status-tag ${statusClass}">${escapeHtml(statusName)}</span></dd>
            <dt>Assigned To</dt><dd>${a.assigned_to ? escapeHtml(a.assigned_to.name || '\u2014') : '<span style="color:var(--color-text-muted)">Not assigned</span>'}</dd>
            <dt>Location</dt><dd>${escapeHtml(a.location?.name || a.rtd_location?.name || '\u2014')}</dd>
            ${wlan ? `<dt>WLAN MAC</dt><dd class="mono">${escapeHtml(wlan)}</dd>` : ''}
            ${lan ? `<dt>LAN MAC</dt><dd class="mono">${escapeHtml(lan)}</dd>` : ''}
            ${ip ? `<dt>IP</dt><dd class="mono">${escapeHtml(ip)}</dd>` : ''}
            ${a.notes ? `<dt>Notes</dt><dd>${escapeHtml(a.notes)}</dd>` : ''}
          </dl>
        </div>
        <div class="sheet-footer sheet-footer--stacked">
          <button class="btn btn-primary btn-block" id="ql-modal-edit">Edit Asset</button>
          <div class="footer-row">
            <button class="btn btn-secondary" style="flex:1" id="ql-modal-close">Close</button>
            <button class="btn btn-secondary" style="flex:1" id="ql-modal-view">View All Fields</button>
          </div>
        </div>
      `;

      sheet.querySelector('#ql-modal-close-top').addEventListener('click', close);
      sheet.querySelector('#ql-modal-close').addEventListener('click', close);
      sheet.querySelector('#ql-modal-view').addEventListener('click', () => renderFullView());
      sheet.querySelector('#ql-modal-edit').addEventListener('click', () => renderEditForm(a.id, a.name));
    }

    // ── Full View — dynamically render every field ──
    function renderFullView() {
      sheet.style.maxHeight = '95dvh';
      const a = currentAsset;

      sheet.innerHTML = `
        <div class="sheet-handle"></div>
        <div class="sheet-header">
          <button class="btn btn-secondary btn-sm" id="ql-full-back">\u2190 Summary</button>
          <h2 style="margin:0; font-size:1rem; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; flex:1;">
            All Fields
          </h2>
          <span style="font-size:0.75rem; color:var(--color-text-muted); white-space:nowrap;" class="mono">${escapeHtml(a.asset_tag || '')}</span>
        </div>
        <div class="sheet-body" id="ql-full-body" style="padding:0 16px 16px;"></div>
        <div class="sheet-footer">
          <button class="btn btn-secondary" style="flex:1" id="ql-full-close">Close</button>
          <button class="btn btn-primary" style="flex:1" id="ql-full-edit">Edit Asset</button>
        </div>
      `;

      sheet.querySelector('#ql-full-back').addEventListener('click', renderDetail);
      sheet.querySelector('#ql-full-close').addEventListener('click', close);
      sheet.querySelector('#ql-full-edit').addEventListener('click', () => renderEditForm(a.id, a.name));

      const body = sheet.querySelector('#ql-full-body');
      body.innerHTML = buildFullViewHtml(a);
    }

    async function renderEditForm(assetId, assetName) {
      sheet.style.maxHeight = '95dvh';
      sheet.innerHTML = `
        <div class="sheet-handle"></div>
        <div class="sheet-header">
          <button class="btn btn-secondary btn-sm" id="ql-edit-back">\u2190 Back</button>
          <h2 style="margin:0; font-size:1rem; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; flex:1;">Edit: ${escapeHtml(assetName || 'Asset')}</h2>
        </div>
        <div class="sheet-body" style="padding:16px; text-align:center;">
          <div class="spinner"></div>
          <p style="margin-top:8px; font-size:0.85rem; color:var(--color-text-muted);">Loading asset\u2026</p>
        </div>
        <div class="sheet-footer">
          <button class="btn btn-secondary" style="flex:1" disabled>Add to Queue</button>
          <button class="btn btn-primary" style="flex:1" disabled>Sync Now</button>
        </div>
      `;
      sheet.querySelector('#ql-edit-back').addEventListener('click', renderDetail);

      let full;
      try {
        full = await getAsset(assetId);
      } catch (err) {
        sheet.querySelector('.sheet-body').innerHTML =
          `<p style="color:var(--color-danger,#ef4444);">Failed to load asset: ${escapeHtml(err.message)}</p>`;
        return;
      }

      // Load models and statuses for dropdowns
      let modelOptions = '<option value="">\u2014 Select \u2014</option>';
      let statusOptions = '<option value="">\u2014 Select \u2014</option>';
      try {
        const [modelsRes, statusRes] = await Promise.all([getModels(), getStatusLabels()]);
        (modelsRes.rows || []).forEach((m) => {
          modelOptions += `<option value="${m.id}">${escapeHtml(m.name || '')}</option>`;
        });
        (statusRes.rows || []).forEach((s) => {
          statusOptions += `<option value="${s.id}">${escapeHtml(s.name || '')}</option>`;
        });
      } catch {
        // Continue with empty dropdowns
      }

      const body = sheet.querySelector('.sheet-body');
      body.style.textAlign = '';
      body.innerHTML = `
        <div style="padding:0 16px 16px;">
          <div class="form-group">
            <label for="ql-edit-name">Asset Name</label>
            <input type="text" id="ql-edit-name" class="form-control" value="${escapeAttr(full.name || '')}" />
          </div>
          <div class="form-group">
            <label for="ql-edit-serial">Serial Number</label>
            <input type="text" id="ql-edit-serial" class="form-control" value="${escapeAttr(full.serial || '')}" />
          </div>
          <div class="form-group">
            <label for="ql-edit-model">Model</label>
            <select id="ql-edit-model" class="form-control">${modelOptions}</select>
          </div>
          <div class="form-group">
            <label for="ql-edit-status">Status</label>
            <select id="ql-edit-status" class="form-control">${statusOptions}</select>
          </div>
          <div class="form-group">
            <label for="ql-edit-wlan">WLAN MAC</label>
            <input type="text" id="ql-edit-wlan" class="form-control" value="${escapeAttr(full.custom_fields?.[MAC_COLUMN_WLAN]?.value || '')}" />
          </div>
          <div class="form-group">
            <label for="ql-edit-lan">LAN MAC</label>
            <input type="text" id="ql-edit-lan" class="form-control" value="${escapeAttr(full.custom_fields?.[MAC_COLUMN_LAN]?.value || '')}" />
          </div>
          <div class="form-group">
            <label for="ql-edit-ip">IP Address</label>
            <input type="text" id="ql-edit-ip" class="form-control" value="${escapeAttr(full.custom_fields?.[MAC_COLUMN_IP]?.value || '')}" />
          </div>
          <div class="form-group">
            <label for="ql-edit-notes">Notes</label>
            <textarea id="ql-edit-notes" class="form-control" rows="2">${escapeHtml(full.notes || '')}</textarea>
          </div>
        </div>
      `;

      // Pre-select model and status
      const modelSel = body.querySelector('#ql-edit-model');
      const statusSel = body.querySelector('#ql-edit-status');
      if (full.model?.id) {
        const opt = Array.from(modelSel.options).find((o) => o.value == full.model.id);
        if (opt) opt.selected = true;
      }
      if (full.status_label?.id) {
        const opt = Array.from(statusSel.options).find((o) => o.value == full.status_label.id);
        if (opt) opt.selected = true;
      }

      // Replace footer with edit actions
      const footer = sheet.querySelector('.sheet-footer');
      footer.innerHTML = `
        <button class="btn btn-secondary" style="flex:1" id="ql-edit-queue">Add to Queue</button>
        <button class="btn btn-primary" style="flex:1" id="ql-edit-sync">Sync Now</button>
      `;

      function buildEditPayload() {
        return {
          asset_id: assetId,
          name: body.querySelector('#ql-edit-name').value.trim(),
          serial: body.querySelector('#ql-edit-serial').value.trim(),
          notes: body.querySelector('#ql-edit-notes').value.trim(),
          ...(modelSel.value ? { model_id: parseInt(modelSel.value, 10) } : {}),
          ...(statusSel.value ? { status_id: parseInt(statusSel.value, 10) } : {}),
          ...(body.querySelector('#ql-edit-wlan').value.trim() ? { [MAC_COLUMN_WLAN]: body.querySelector('#ql-edit-wlan').value.trim() } : {}),
          ...(body.querySelector('#ql-edit-lan').value.trim() ? { [MAC_COLUMN_LAN]: body.querySelector('#ql-edit-lan').value.trim() } : {}),
          ...(body.querySelector('#ql-edit-ip').value.trim() ? { [MAC_COLUMN_IP]: body.querySelector('#ql-edit-ip').value.trim() } : {}),
        };
      }

      footer.querySelector('#ql-edit-queue').addEventListener('click', async () => {
        await enqueue('update_asset', buildEditPayload());
        hapticSuccess();
        showToast('Asset update queued');
        window.dispatchEvent(new CustomEvent('queue-updated'));
        close();
      });

      footer.querySelector('#ql-edit-sync').addEventListener('click', async () => {
        const syncBtn = footer.querySelector('#ql-edit-sync');
        syncBtn.disabled = true;
        syncBtn.textContent = 'Syncing\u2026';
        try {
          await syncDirect('update_asset', buildEditPayload());
          hapticSuccess();
          showToast('Asset updated');
          close();
        } catch (err) {
          showToast('Sync failed: ' + err.message);
          syncBtn.disabled = false;
          syncBtn.textContent = 'Sync Now';
        }
      });
    }

    renderDetail();

    backdrop.addEventListener('click', close);
    sheet.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });

    requestAnimationFrame(() => {
      backdrop.classList.add('open');
      sheet.classList.add('open');
    });
  }

  // ── Not Found Modal ──
  function showNotFoundModal(tag) {
    const backdrop = document.createElement('div');
    backdrop.className = 'bottom-sheet-backdrop';
    const sheet = document.createElement('div');
    sheet.className = 'bottom-sheet';
    document.body.appendChild(backdrop);
    document.body.appendChild(sheet);

    function close() {
      backdrop.classList.remove('open');
      sheet.classList.remove('open');
      setTimeout(() => { backdrop.remove(); sheet.remove(); }, 300);
    }

    sheet.innerHTML = `
      <div class="sheet-handle"></div>
      <div class="sheet-header">
        <h2>Not Found</h2>
      </div>
      <div class="sheet-body" style="padding:24px 16px; text-align:center;">
        <p style="font-size:0.9rem; color:var(--color-text-muted); margin:0;">
          No asset found matching '${escapeHtml(tag)}'
        </p>
      </div>
      <div class="sheet-footer">
        <button class="btn btn-secondary" style="flex:1" id="ql-nf-close">Close</button>
        <button class="btn btn-primary" style="flex:1" id="ql-nf-add">Add Asset \u2192</button>
      </div>
    `;

    sheet.querySelector('#ql-nf-close').addEventListener('click', close);
    sheet.querySelector('#ql-nf-add').addEventListener('click', () => {
      close();
      sessionStorage.setItem('prefill-tag', tag);
      router.navigate('/add-asset');
    });

    backdrop.addEventListener('click', close);
    sheet.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });

    requestAnimationFrame(() => {
      backdrop.classList.add('open');
      sheet.classList.add('open');
    });
  }

  return () => {
    stopScanner();
  };
}

// ── Full View builder — dynamically renders all API fields ──

// Keys to skip in the top-level rendering (internal/redundant/UI-control)
const SKIP_KEYS = new Set([
  'available_actions', 'user_can_checkout', 'id', 'qr', 'alt_barcode',
]);

// Human-readable labels for known Snipe-IT fields
const FIELD_LABELS = {
  name: 'Name',
  asset_tag: 'Asset Tag',
  serial: 'Serial',
  model_number: 'Model Number',
  model: 'Model',
  category: 'Category',
  manufacturer: 'Manufacturer',
  status_label: 'Status',
  supplier: 'Supplier',
  company: 'Company',
  assigned_to: 'Assigned To',
  location: 'Location',
  rtd_location: 'Default Location',
  purchase_date: 'Purchase Date',
  purchase_cost: 'Purchase Cost',
  order_number: 'Order Number',
  warranty_months: 'Warranty (months)',
  warranty_expires: 'Warranty Expires',
  notes: 'Notes',
  image: 'Image',
  eol: 'EOL',
  eol_date: 'EOL Date',
  age: 'Age',
  last_checkout: 'Last Checkout',
  last_checkin: 'Last Check-in',
  expected_checkin: 'Expected Check-in',
  last_audit_date: 'Last Audit',
  next_audit_date: 'Next Audit',
  created_at: 'Created',
  updated_at: 'Updated',
  deleted_at: 'Deleted',
  checkin_counter: 'Check-in Count',
  checkout_counter: 'Checkout Count',
  requests_counter: 'Request Count',
  book_value: 'Book Value',
  byod: 'BYOD',
  requestable: 'Requestable',
  custom_fields: 'Custom Fields',
};

// Display sections and which keys go in each
const SECTIONS = [
  {
    title: 'Identity',
    keys: ['name', 'asset_tag', 'serial', 'model_number', 'image'],
  },
  {
    title: 'Hardware',
    keys: ['model', 'category', 'manufacturer', 'status_label', 'eol', 'eol_date', 'byod'],
  },
  {
    title: 'Assignment & Location',
    keys: ['assigned_to', 'location', 'rtd_location', 'company', 'last_checkout', 'last_checkin', 'expected_checkin'],
  },
  {
    title: 'Purchase & Warranty',
    keys: ['supplier', 'purchase_date', 'purchase_cost', 'order_number', 'warranty_months', 'warranty_expires', 'book_value'],
  },
  {
    title: 'Activity',
    keys: ['checkin_counter', 'checkout_counter', 'requests_counter', 'age', 'last_audit_date', 'next_audit_date'],
  },
  {
    title: 'System',
    keys: ['created_at', 'updated_at', 'deleted_at', 'requestable', 'notes'],
  },
];

function buildFullViewHtml(asset) {
  let html = '';
  const renderedKeys = new Set();

  // Render defined sections
  for (const section of SECTIONS) {
    const rows = [];
    for (const key of section.keys) {
      if (!(key in asset)) continue;
      renderedKeys.add(key);
      const val = asset[key];
      const display = formatFieldValue(key, val);
      if (display === null) continue; // skip empty
      const label = FIELD_LABELS[key] || humanizeKey(key);
      rows.push(`<dt>${escapeHtml(label)}</dt><dd>${display}</dd>`);
    }
    if (rows.length > 0) {
      html += `<div class="full-view-section-label">${escapeHtml(section.title)}</div>`;
      html += `<dl class="asset-detail-dl">${rows.join('')}</dl>`;
    }
  }

  // Custom fields section — keys are human-readable names, .field is the DB column
  if (asset.custom_fields && typeof asset.custom_fields === 'object') {
    renderedKeys.add('custom_fields');
    const cfRows = [];
    for (const [displayName, fieldObj] of Object.entries(asset.custom_fields)) {
      const val = fieldObj?.value;
      if (val === null || val === undefined || val === '') continue;
      // Use the object key as label — it's already human-readable (e.g. "WLAN MAC Address")
      const label = displayName;
      const isMono = looksLikeTechnicalValue(String(val));
      cfRows.push(`<dt>${escapeHtml(label)}</dt><dd${isMono ? ' class="mono"' : ''}>${escapeHtml(String(val))}</dd>`);
    }
    if (cfRows.length > 0) {
      html += `<div class="full-view-section-label">Custom Fields</div>`;
      html += `<dl class="asset-detail-dl">${cfRows.join('')}</dl>`;
    }
  }

  // Catch any remaining top-level keys not in sections
  const extraRows = [];
  for (const key of Object.keys(asset)) {
    if (renderedKeys.has(key) || SKIP_KEYS.has(key)) continue;
    renderedKeys.add(key);
    const val = asset[key];
    const display = formatFieldValue(key, val);
    if (display === null) continue;
    const label = FIELD_LABELS[key] || humanizeKey(key);
    extraRows.push(`<dt>${escapeHtml(label)}</dt><dd>${display}</dd>`);
  }
  if (extraRows.length > 0) {
    html += `<div class="full-view-section-label">Other</div>`;
    html += `<dl class="asset-detail-dl">${extraRows.join('')}</dl>`;
  }

  return html || '<p style="color:var(--color-text-muted); text-align:center; padding:20px;">No data available</p>';
}

/**
 * Format a single field value for display. Returns HTML string or null to skip.
 */
function formatFieldValue(key, val) {
  if (val === null || val === undefined || val === '') return null;

  // Nested object with .name (model, category, manufacturer, supplier, etc.)
  if (typeof val === 'object' && val !== null && !Array.isArray(val)) {
    // assigned_to — varies by type (user, location, asset)
    if (key === 'assigned_to') {
      const name = val.name || val.username || '\u2014';
      const type = val.type || '';
      let extra = '';
      if (type === 'user' && val.username) {
        extra = `<span style="display:block; font-size:0.7rem; color:var(--color-text-placeholder); margin-top:1px;">@${escapeHtml(val.username)}${val.employee_num ? ' \u2022 ' + escapeHtml(val.employee_num) : ''}</span>`;
      } else if (type === 'asset' && val.asset_tag) {
        extra = `<span style="display:block; font-size:0.7rem; color:var(--color-text-placeholder); margin-top:1px;" class="mono">${escapeHtml(val.asset_tag)}</span>`;
      } else if (type) {
        extra = `<span style="display:block; font-size:0.7rem; color:var(--color-text-placeholder); margin-top:1px;">${escapeHtml(type)}</span>`;
      }
      return escapeHtml(String(name)) + extra;
    }

    // Status label — render as badge
    if (key === 'status_label' && val.name) {
      const statusType = val.status_type || '';
      const cls = statusType === 'deployable' ? 'deployable'
        : statusType === 'pending' ? 'pending'
        : statusType === 'archived' ? 'archived' : '';
      return `<span class="status-tag ${cls}">${escapeHtml(val.name)}</span>`;
    }

    if ('name' in val && val.name) {
      return escapeHtml(String(val.name));
    }
    // Date objects from Snipe-IT: { datetime, formatted }
    if ('datetime' in val && val.formatted) {
      return escapeHtml(String(val.formatted));
    }
    if ('datetime' in val && val.datetime) {
      return `<span class="mono">${escapeHtml(String(val.datetime))}</span>`;
    }
    // Fallback: show as compact JSON if it has content
    const str = JSON.stringify(val);
    if (str === '{}' || str === '[]') return null;
    return `<span class="mono" style="font-size:0.75rem; word-break:break-all;">${escapeHtml(str)}</span>`;
  }

  // Boolean — render as small badge
  if (typeof val === 'boolean') {
    return val
      ? '<span class="status-tag deployable">Yes</span>'
      : '<span class="status-tag">No</span>';
  }

  // Number
  if (typeof val === 'number') {
    return escapeHtml(String(val));
  }

  // String
  const str = String(val);
  if (str.trim() === '') return null;

  // Image URLs — show as thumbnail
  if (key === 'image' && (str.startsWith('http://') || str.startsWith('https://'))) {
    return `<img src="${escapeAttr(str)}" alt="${escapeAttr(key)}" style="max-width:120px; max-height:80px; border-radius:4px; border:1px solid var(--color-border);" loading="lazy" />`;
  }

  // Technical-looking values get mono styling
  if (looksLikeTechnicalValue(str)) {
    return `<span class="mono">${escapeHtml(str)}</span>`;
  }

  return escapeHtml(str);
}

/**
 * Convert snake_case/camelCase key to a readable label.
 */
function humanizeKey(key) {
  return key
    .replace(/^_snipeit_/, '')
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Heuristic: does this string look like a MAC address, IP, serial, UUID, etc.?
 */
function looksLikeTechnicalValue(str) {
  // MAC address
  if (/^([0-9A-Fa-f]{2}[:-]){5}[0-9A-Fa-f]{2}$/.test(str)) return true;
  // IP address
  if (/^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(str)) return true;
  // UUID
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str)) return true;
  return false;
}

function escapeHtml(str) {
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
}

function escapeAttr(str) {
  return String(str).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
